import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener, ElementRef, } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { Title } from "@angular/platform-browser";
import { tap, Observable, concat, merge, of, from, Subscription, filter, Subject, throwError, forkJoin } from 'rxjs';
import { map, finalize, switchMap, concatMap, debounceTime, distinctUntilChanged, catchError, takeUntil } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { saveAs } from 'file-saver';
import { Accordion } from 'primeng/accordion';
import { FilterService, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';

import { environment } from 'src/environments/environment';

import { GraphData } from 'src/app/shared/dashboard';
import { Product, ProductInfo, ProdVendors, ProductStat, ProductBooking, ApprovedCustomsCodes } from 'src/app/shared/product'
import { ProductGroup } from 'src/app/shared/product-group';
import { DocTypes } from 'src/app/shared/doc-list-entry';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard'

@Component({
  selector: 'app-edit-product',
  templateUrl: './edit-product.component.html',
  styleUrls: ['./edit-product.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Product editor with an 11-section accordion layout and tab-router navigation.
 *
 * Loading uses `forkJoin` for three parallel requests after the base product is fetched:
 * `getActcList` (approved customs codes), `getProdInfoById` (price/stock summary), and
 * `getProductBookingEx` (booking/reservation data). All are loaded in `loadProductContent`
 * via a single `concatMap` + `forkJoin` chain with `takeUntil(destroy$)`.
 *
 * Product-code uniqueness is validated in real time via `prodCodeCheck$` — a `Subject<string>`
 * piped through `debounceTime(300)` + `distinctUntilChanged` + `switchMap(getProductByCode)`.
 *
 * `canEditPrice` is role-gated: requires `accessGroup >= 10000` or the `portal-admin` /
 * `can-manage-products` role. The price-calculation tab entry in `tabRouter` propagates
 * this gate through its `disabled` callback.
 *
 * `onAskEditing` checks two document queries before allowing the product code to be edited:
 * first for stock-movement documents (DOCTYPE 2–5), then for internal transfer documents
 * identified by `DOCNOTE2` containing the product code.
 *
 * `onConfirm(4)` falls through to `case 1` — case 4 is used to trigger a combined
 * data + price save from `onSetMainPriceRow`.
 */
export class EditProductComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  globalSettinsData: GlobalSettingsData;

  /*private*/ _prodId: number;

  _copyFrom: number;

  product: Product;
  productCopy: Product = null;
  /**
   * Last product state known to be loaded from, or successfully saved to, the server (B-40).
   * Separate from `productCopy`, which the save pipeline also refreshes in `finalize` when a save
   * fails, so it can hold unsaved edits.
   */
  lastConfirmedProduct: Product = null;

  kickChanges: Product = null;

  parentProducts: Product[] = null;
  private parentProductCheck$ = new Subject<number>();
  private parentProductQuery: Subscription = null;

  notReady: boolean = true;

  search$: Observable<string> = new Observable<string>();
  private prodCodeCheck$ = new Subject<string>();
  private _prodCodeQuery: Subscription = null;
  
  prodCodeValid: boolean = true;

  selectedProdVendor: ProdVendors = null;

  booking: ProductBooking[] = null;
  selectedBooking: ProductBooking = null;

  prodInfo: ProductInfo;

  productAnalytic: ProductStat[] = [];
  dataProductAnalytic: GraphData = null;
  analiticsIsReady: boolean = false;

  @ViewChild('editProdAcc') editProdAcc: Accordion;

  tabActiveState: boolean[] = [true, false, false, false, false, false, false, false, false, false, false, false];

  /** `true` when the user's access group allows viewing the price-calculation tab. */
  public get tabPriceEnable(): boolean { return this.globalSettinsData.accessGroup >= 10000; }
  /** `true` when the user's access group allows viewing the analytics tab. */
  public get tabAnalyticEnable(): boolean { return this.globalSettinsData.accessGroup >= 10000; }
  /** `true` when the user's access group allows viewing the turnover tab. */
  public get tabTurnoverEnable(): boolean { return this.globalSettinsData.accessGroup >= 10000; }

  /** `true` when the product is a simple (non-composite) item (`PRODKIND == 0`). */
  public get productIsSimple(): boolean {
    return (this.product) && (this.product.PRODKIND == 0);
  }

  private _productQuery: Subscription = null;
  private _prodInfoQuery: Subscription = null;
  private _productBookingQuery: Subscription = null;
  private _prodPutQuery: Subscription = null;

  /** `true` when `PRODNAME` has more than 3 characters. */
  public get prodNameValid(): boolean { return (this.product && ((this.product.PRODNAME ?? '').trim().length > 3)); }
  /** `true` when `PRODDESCRIPTION` is absent or under 255 characters. */
  public get prodDescValid(): boolean { return (!this.product || !this.product.PRODDESCRIPTION || (this.product.PRODDESCRIPTION.trim().length < 255)); }
  /** `true` when `PRODNOTE` is absent or under 255 characters. */
  public get prodNoteValid(): boolean { return (!this.product || !this.product.PRODNOTE || (this.product.PRODNOTE.trim().length < 255)); }
  /** `true` when a product group is assigned. */
  public get prodGroupValid(): boolean { return (this.product && this.product.PRODGROUP) ? true : false; }
  /** `true` when a sales manager (`PRODSALERID`) is assigned. */
  public get prodManagerValid(): boolean { return (this.product && this.product.PRODSALERID) ? true : false; }
  /** `true` when a vendor (`PRODVENDORID`) is assigned. */
  public get prodVendorValid(): boolean { return (this.product && this.product.PRODVENDORID) ? true : false; }

  /** `true` when any required field is missing or invalid — disables the save button. */
  public get formInvalid(): boolean {
    return (
      !this.product ||
      !this.prodCodeValid ||
      !this.prodGroupValid ||
      !this.prodManagerValid ||
      !this.prodVendorValid ||
      !this.prodNameValid ||
      (this.product.PRODCODE.trim().length < 3)
      );
  }

  /** Returns `true` when the given product property differs from its copy (used by template). */
  public hasChangedProperty(prop: string): boolean {
    return (this.product && this.productCopy) ? (this.product[prop] != this.productCopy[prop]) : false;
  }

  /** `true` when no product property has changed; excludes `_PRODPARTS`, `_PRODVENDORS`, `ACTC_LIST`. */
  public get noChangesThere(): boolean {
    let equal: boolean = (this.productCopy ? true : false);

    if (equal) {
      for (var props in this.product) {
        if (!['_PRODPARTS', '_PRODVENDORS', 'ACTC_LIST'].includes(props)) {
//          true || environment.production || console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.product[props] + '/' + this.productCopy[props]);
          equal = (this.product[props] == this.productCopy[props]);
          if (!equal) {
            // environment.production || console.log('*** EditProductComponent in noChangesThere: not equal: ' + props + '/' + this.product[props] + '/' + this.productCopy[props]);
            break;
          }
        }
      }
//      true || environment.production || console.log('*** EditProductComponent in noChangesThere: ' + equal);
    }

    return equal;
  }

  // Products are created via "Скопировать" only. The two items below stay disabled with no
  // handler/route by owner decision (B-47, 2026-10-02).
  items: MenuItem[] = [
    { label: 'Новый товар...', icon: 'pi pi-plus', disabled: true },
    { label: 'Удалить товар...', icon: 'pi pi-trash', disabled: true },
  ];

  productApprovedCustomsCodesList: { ApprovedCustomsCodes, disabled: boolean }[] = [];

  /** `true` when the first approved customs code matches the current `PRODCUSTCODE`. */
  public get customsCodeApproved(): boolean {
    return (
      this.product && ((this.product.ACTC_LIST || []).length > 0) && (this.product.ACTC_LIST[0].ACTC_STRING == this.product.PRODCUSTCODE)
    );
  }

  @ViewChild('aTabMainInfo') aTabMainInfo: ElementRef;
  @ViewChild('atabProdInfo') atabProdInfo: ElementRef;
  @ViewChild('atabBillOfMaterial') atabBillOfMaterial: ElementRef;
  @ViewChild('atabParentProducts') atabParentProducts: ElementRef;
  @ViewChild('atabBooking') atabBooking: ElementRef;
  @ViewChild('atabAnalytic') atabAnalytic: ElementRef;
  @ViewChild('atabPriceCalculation') atabPriceCalculation: ElementRef;
  @ViewChild('atabRowInfo') atabRowInfo: ElementRef;
  @ViewChild('atabAllStocks') atabAllStocks: ElementRef;
  @ViewChild('atabTurnOverSheet') atabTurnOverSheet: ElementRef;
  @ViewChild('atabAllDocuments') atabAllDocuments: ElementRef;
  @ViewChild('atabSerials') atabSerials: ElementRef;

  tabRouter: any[] = [];

  navigationMenu: MenuItem[] = [{
    label: 'Навигация по товару',
    icon: 'pi pi-home',
    command: () => { },
    enabled: true,
    disabled: false,
    items: []
  }];

  swapLeftToRight: boolean = true;

  fullURL: string = window.location.href;
  baseURL: string = window.location.href;
  hostname: string = window.location.hostname;
  origin: string = window.location.origin;

  selectedSection: string = 'main';

  editCodeAllowed: boolean = false;

  private destroy$ = new Subject<void>();

  /** `true` when the user may edit pricing — requires high access group or admin/product-manager role. */
  public get canEditPrice(): boolean {
    return (this.globalSettinsData?.accessGroup >= 10000) || this.globalsettingsservice.hasSomeRoles('portal-admin', 'can-manage-products');
  }

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private docApi: DocumentApiService,
    private filterService: FilterService
  ) {
    this.tabRouter = [
      { label: 'Описание товара', icon: 'pi pi-fw pi-file-edit', anchor: () => { return this.aTabMainInfo; }, section: 'main', activateTab: () => { this.tabActiveState[0] = true; }, disabled: () => { return false; } },
      { label: 'Цена и наличие', icon: 'pi pi-fw pi-shop', anchor: () => { return this.atabProdInfo; }, section: 'info', activateTab: () => { this.tabActiveState[0] = true; }, disabled: () => { return false; } },
      { label: 'Состав', icon: 'pi pi-fw pi-wrench', anchor: () => { return this.atabBillOfMaterial; }, section: 'bom', activateTab: () => { this.tabActiveState[1] = true; }, disabled: () => { return false; } },
      { label: 'Используется в товарах', icon: 'pi pi-fw pi-list', anchor: () => { return this.atabParentProducts; }, section: 'use', activateTab: () => { this.tabActiveState[9] = true; }, disabled: () => { return false; } },
      { label: 'Резервирование и склад', icon: 'pi pi-fw pi-users', anchor: () => { return this.atabBooking; }, section: 'booking', activateTab: () => { this.tabActiveState[2] = true; }, disabled: () => { return false; } },
      { label: 'Аналитика', icon: 'pi pi-fw pi-chart-bar', anchor: () => { return this.atabAnalytic; }, section: 'history', activateTab: () => { this.tabActiveState[3] = true; }, disabled: () => { return false; } },
      { label: 'Расчет цены и поставщики', icon: 'pi pi-fw pi-address-book', anchor: () => { return this.atabPriceCalculation; }, section: 'price', activateTab: () => { this.tabActiveState[4] = true; }, disabled: () => { return !this.canEditPrice; } },
      { label: 'Служебная информация', icon: 'pi pi-fw pi-question', anchor: () => { return this.atabRowInfo; }, section: 'row', activateTab: () => { this.tabActiveState[5] = true; }, disabled: () => { return false; } },
      { label: 'Остатки по всем складам', icon: 'pi pi-fw pi-warehouse', anchor: () => { return this.atabAllStocks; }, section: 'stock', activateTab: () => { this.tabActiveState[7] = true; }, disabled: () => { return false; } },
      { label: 'Оборотная ведомость', icon: 'pi pi-fw pi-database', anchor: () => { return this.atabTurnOverSheet; }, section: 'trans', activateTab: () => { this.tabActiveState[6] = true; }, disabled: () => { return false; } },
      { label: 'Все документы', icon: 'pi pi-fw pi-file', anchor: () => { return this.atabAllDocuments; }, section: 'documents', activateTab: () => { this.tabActiveState[8] = true; }, disabled: () => { return false; } },
      { label: 'Серийные номера', icon: 'pi pi-fw pi-barcode', anchor: () => { return this.atabSerials; }, section: 'serials', activateTab: () => { this.tabActiveState[11] = true; }, disabled: () => { return false; } },
    ];
  }

  /** Guards navigation; blocks when there are unsaved product changes. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.noChangesThere;
  }

  /** Signals `destroy$` to auto-unsubscribe all `takeUntil` pipelines. */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads the product via `getProductById`, then runs a `forkJoin` for `getActcList`,
   * `getProdInfoById`, and `getProductBookingEx` in parallel. `finalize` snapshots
   * `productCopy`, sets up the navigation menu, and triggers `parentProductCheck$`.
   */
  loadProductContent(): void {
    if (!this._prodId) {
      // Handle new product creation
      return;
    }

    this.notReady = true;

    // Step 1: Get base product first (required by all others)
    this._productQuery = this.prodApi.getProductById(this._prodId)
      .pipe(
        takeUntil(this.destroy$), // Auto-unsubscribe on destroy
        filter(prod => prod && prod.length > 0),
        map(prod => this.product = prod[0]),

        // Step 2: Fetch independent data in parallel
        concatMap(prod =>
          forkJoin({
            actcList: this.prodApi.getActcList(prod).pipe(
              map(actc => actc.map(e => ({ ...e, disabled: true }))),
              catchError(err => {
                this.handleError('Load Customs Codes')(err);
                return of([]); // Return empty array on error
              })
            ),

            prodInfo: this.prodApi.getProdInfoById(
              prod.PRODID,
              this.globalSettinsData.mainCompanyId,
              this.globalSettinsData.balanceCurrencyId,
              this.globalSettinsData.mainStockId,
              this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
              this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
              new Date()
            ).pipe(
              map(data => data[0]),
              catchError(err => {
                this.handleError('Load Product Info')(err);
                return of(null);
              })
            ),

            booking: this.prodApi.getProductBookingEx(
              prod.PRODID,
              this.globalSettinsData.mainCompanyId,
              this.globalSettinsData.manDepartmentId,
              new Date()
            ).pipe(
              catchError(err => {
                this.handleError('Load Booking Data')(err);
                return of([]);
              })
            )
          }).pipe(
            // All parallel requests complete here
            tap(({ actcList, prodInfo, booking }) => {
              this.product.ACTC_LIST = actcList;
              this.prodInfo = prodInfo;
              this.booking = booking;
            })
          )
        ),

        catchError(this.handleError('Load Product')),

        finalize(() => {
          if (this.product) {
            this.titleService.setTitle(`Редактирование товара: ${this.product.PRODCODE}`);
            this.productCopy = { ...this.product };
            this.lastConfirmedProduct = { ...this.product };
            this.kickChanges = { ...this.product };
            this.editCodeAllowed = this.product.PRODKIND == 0;
            this.parentProductCheck$.next(this.product.PRODID);

            // Setup navigation
            const [hash, page, did, sec] = window.location.hash.split('/');
            this.baseURL = `${window.location.protocol}//${window.location.hostname}:${window.location.port}/${hash}/${page}/${did}/`;
            this.updateNavigationMenu(page);
          }

          this.notReady = false;
        })
      )
      .subscribe({
        error: (err) => {
          this.notReady = false;
          console.error('*** EditProductComponent loadProductContent: Fatal error', err);
        }
      });
  }

  private updateNavigationMenu(page: string): void {
    this.navigationMenu[0].items = this.tabRouter.map(e => ({
      label: e.label,
      icon: e.icon,
      command: () => this.router.navigate([page, this._prodId, e.section]),
      enabled: true,
      disabled: e.disabled()
    }));
    this.navigationMenu = [...this.navigationMenu];
  }

  /** Activates the tab for the given section name and scrolls to its anchor element. */
  routeByParameters(selectedSection: string) {
    environment.production || console.log('*** EditProductComponent  in routeByParameters: tabRouter: ' + JSON.stringify(this.tabRouter));
    console.log('*** EditProductComponent in routeByParameters: selectedSection: ' + selectedSection);

    let route = this.tabRouter.find(s => s.section == selectedSection);
    environment.production || console.log('*** EditProductComponent  in routeByParameters: found section: ' + JSON.stringify(route));
    if (route) {
      environment.production || console.log('*** EditProductComponent in routeByParameters: route: ' + JSON.stringify(route));

      route.activateTab();

      // Scroll to the anchor element after a short delay to ensure the view is ready
      setTimeout(() => {
        if (route.anchor()) {
          route.anchor().nativeElement.scrollIntoView({ behavior: 'smooth' });
          this.selectedSection = selectedSection;
        }
      }, 200);
    }
  }

/** Loads monthly stock/sales analytics via `getProdStatById` and populates `dataProductAnalytic`. */
loadProductAnalyticsGraphData() {
    if (this._prodId && this.tabAnalyticEnable) {
/*
 *  getProdStatById(id: number, cm: number, dp: number, pr: number, s1: number, s2: number, s3: number, fd: Date): Observable<ProductStat> {
 */
      this.prodApi.getProdStatById(
        this._prodId, 
        this.globalSettinsData.mainCompanyId,
        6, 2, 
        this.globalSettinsData.mainStockId,
        this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
        this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
        new Date()
      )
        .pipe(map((data: ProductStat[]) => this.productAnalytic = data))
      .subscribe(
        () => {
          console.log('this.restapi.getCrossRateGraphData in Subscribe')

          if (this.productAnalytic) {
            //              console.log(this.crossRateGraphData);
            this.dataProductAnalytic = {
              labels: [],
              datasets: [
                {
                  label: 'Склад на начало периода',
                  data: [],
                  fill: false,
                  borderColor:     '#025529',
                  type: 'line'
                },
                {
                  label: 'Поступления на склад за период',
                  data: [],
                  fill: true,
                  backgroundColor: '#022A95'
                },
                {
                  label: 'Отгрузки со склада за период',
                  data: [],
                  fill: true,
                  backgroundColor: '#AB8002'
                },
                {
                  label: 'Оплачено за период',
                  data: [],
                  fill: true,
                  backgroundColor: '#617F02'
                },
                {
                  label: 'Выписано за период',
                  data: [],
                  fill: true,
                  backgroundColor: '#177622'
                }

              ]
            }
            //console.log(this.data);

            this.productAnalytic.forEach((element, index) => {
              //console.log(index);
              //console.log(element);
            
              this.dataProductAnalytic.labels.push(formatDate(element.GRAFDATE, 'MM/yyyy', 'en-US'/*, 'GMT+2'*/));
              this.dataProductAnalytic.datasets[0].data.push(element.GRAFVALUE);
              this.dataProductAnalytic.datasets[1].data.push(element.GRAFVALUE1);
              this.dataProductAnalytic.datasets[2].data.push(element.GRAFVALUE2);
              this.dataProductAnalytic.datasets[3].data.push(element.GRAFVALUE3);
              this.dataProductAnalytic.datasets[4].data.push(element.GRAFVALUE4);
            });
            this.analiticsIsReady = true;
            this.messageService.add({ severity: 'info', summary: 'Product Analytics loaded', 'detail': 'Done' });
            //console.log(this.data);
          }
        });
    }
  }

  /**
   * Wires `parentProductCheck$` and `prodCodeCheck$` Subjects, subscribes to
   * `getObservableGlobalSettings` → `route.paramMap` to reload on navigation changes,
   * and triggers `loadProductContent` + `loadProductAnalyticsGraphData`.
   */
  ngOnInit(): void {
    console.log('*** EditProductComponent in ngOnInit')

    //FilterUtils['inCollection'] = (value: number, filter: DocTypes[]) => (filter.find(e => e.DOCTYPEID == value));
    this.filterService.register('inCollection', (value: number, filter: DocTypes[]) => (filter.find(e => e.DOCTYPEID == value)));

    //     this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.parentProducts = null;

    this.parentProductQuery = this.parentProductCheck$.pipe(
      filter(prod => (prod != null)),
      concatMap(prodid => this.prodApi.getProductsByParentProduct(prodid)),
      concatMap(prod => this.parentProducts = [...prod])
    ).subscribe({
      error: err => console.error('*** EditProductComponent in parentProductQuery: ' + JSON.stringify(err))
    });

    this._prodCodeQuery =
      this.prodCodeCheck$.pipe(
        debounceTime(300),
        distinctUntilChanged(),

        tap(() => this.prodCodeValid = true),  // Reset validity while checking (no message by default, but could be used to show "checking..." state))

        switchMap(prodCode => this.prodApi.getProductByCode(prodCode.trim(), true, true).pipe(map(data => {
          let exist = data.length;

          environment.production || console.log('*** ProductListComponent in checkProdCodeValidity: subscription=(' + exist + ')');
          environment.production || console.table(data);

          this.prodCodeValid =
            (this.product.PRODCODE.trim().length > 0) &&
            (
              (this._prodId && (!exist || (this._prodId == data[0].PRODID))) ||
              ((this._prodId == 0) && (exist == 0))
            )
            ;
          console.log('*** ProductListComponent checkProdCodeValidity: ' + this.prodCodeValid);
        })))
      )
        .subscribe({
          error: err => console.error('*** EditProductComponent in prodCodeQuery: ' + JSON.stringify(err))
        }); 

    this.globalsettingsservice.getObservableGlobalSettings().pipe(
      takeUntil(this.destroy$),

      map(settings => {
        this.globalSettinsData = settings;
      }),

      catchError(err => {
        console.error('*** EditProductComponent in getObservableGlobalSettings: ', err);
        return of(null);
      }),

      switchMap(() => this.route.paramMap),
    )
      .subscribe({
        next: (params: ParamMap) => {
          this._prodId = +params.get('id');
          environment.production || console.log('*** EditProductComponent in param subscribe: (' + this._prodId + ')');

          this.loadProductContent();

          if (params.get('section')) {
            environment.production || console.log('*** EditProductComponent in param subscribe: (' + params.get('section') + ')');
            setTimeout(() => this.routeByParameters(params.get('section')), 100);
          }
          this.loadProductAnalyticsGraphData();
        },
        error: err => console.error('*** EditProductComponent subscribe error: ' + JSON.stringify(err))
      });
  }

  /** Logs the accordion tab index that was opened. */
  onTabOpen(event) {
    console.log('*** EditProductComponent in onTabOpen: ' + event.index);

    switch (event.index) {
      case 1:
        break;

      case 2:
        break;

      case 3:
        break;

    }
  }

  /** Navigates to the item-turnover report page for this product. */
  onGoToTurnoverPage(event) {
    console.log('*** EditProductComponent in buttonClick: ' + JSON.stringify(event));

    this.router.navigate(['/show-itemturnoverreport', this._prodId]);
  }

  /** Reset handler (currently a no-op stub). */
  resetClick(event) {
    console.log('*** EditProductComponent in buttonClick: ' + JSON.stringify(event));
  }

  /** Applies the selected vendor ID to `product.PRODVENDORID`. */
  prodVendorSelected(selectedId) {
    console.log('*** EditProductComponent in prodVendorSelected: ' + selectedId);
    this.product.PRODVENDORID = selectedId;
  }

  /** Applies the selected product group ID to `product.PRODGROUP`. */
  prodGroupSelected(selected: ProductGroup) {
    console.log('*** EditProductComponent in prodGroupSelected: ' + selected.PGROUPID);
    this.product.PRODGROUP = selected.PGROUPID;
    environment.production || console.table(this.product);
  }

  /** Applies the selected sales manager ID to `product.PRODSALERID`. */
  prodSalerSelected(selectedId) {
    console.log('*** EditProductComponent in prodVendorSelected: ' + selectedId);
    this.product.PRODSALERID = selectedId;
  }

  /** Sets `PRODKIND` to 1 (composite) or 0 (simple) from the kind toggle. */
  onKindChange(event) {
    console.log('*** EditProductComponent in onKindChange: ' + event.checked);
    this.product.PRODKIND = event.checked ? 1 : 0;
  }

  /** Sets `PRODOBSOLETE` to 1 (obsolete) or 0 (active) from the status toggle. */
  onStatusChange(event) {
    console.log('*** EditProductComponent in onStatusChange: ' + event.checked);
    this.product.PRODOBSOLETE = event.checked ? 1 : 0;
  }

  /** Logs the selected booking/vendor row (diagnostic). */
  onRowSelect(event) {
    console.log('*** EditProductComponent onRowSelect: ' + JSON.stringify(event.data));
  }

  /** No-op handler for row unselection. */
  onRowUnselect(event) { }

  /** Delegates to `onConfirm(1)` to save the product. */
  buttonSaveAllClick(event) {
    console.log('*** EditProductComponent buttonSaveAllClick: ' + JSON.stringify(event))
    //console.log('*** EditProductComponent buttonSaveAllClick: product ' + JSON.stringify(this.product))
    //console.log('*** EditProductComponent buttonSaveAllClick: productCopy ' + JSON.stringify(this.productCopy))

    this.onConfirm(1);
  }

  /**
   * Saves the product. Case 4 falls through to case 1. PUT existing (`_prodId > 0`) and
   * conditionally calls `putProductCategoryByID` if `PRODCATEGORY` changed. POST new products
   * and immediately calls `loadProductContent` to reload with the new ID.
   */
  onConfirm(e: number) {
    console.log('*** EditProductComponent onConfirm: ' + e)

    switch (e) {
      case 4:
        console.log('*** EditProductComponent onConfirm: (Price info) ');

      case 1:
        console.log('*** EditProductComponent onConfirm: (Main data) ');
        // console.log('*** EditProductComponent buttonSaveAllClick: product ' + JSON.stringify(this.product));
        // console.log('*** EditProductComponent buttonSaveAllClick: productCopy ' + JSON.stringify(this.productCopy));

        if (this._prodId) {
          // Snapshot of what is being sent; becomes the export baseline only if the whole save succeeds.
          const savedSnapshot: Product = { ...this.product };
          this._prodPutQuery =
            this.prodApi.putProductMainInfo(this.product).pipe(

              tap(cli => console.log('*** EditProductComponent onConfirm: saved ' + JSON.stringify(cli))),

              filter(() => this.productCopy.PRODCATEGORY != this.product.PRODCATEGORY),

              concatMap(prod => this.prodApi.putProductCategoryByID(this.product)),

              finalize(() => {
                this.titleService.setTitle('Редактирование товара: ' + this.product.PRODCODE);
                this.productCopy = { ... this.product };

                this.kickChanges = { ...this.product };

                console.log('*** EditProductComponent onConfirm: *** PUT *** ');
              })
            )
              .subscribe({ complete: () => { this.lastConfirmedProduct = savedSnapshot; } });
        } else {
          this._prodPutQuery =
            this.prodApi.postNewProductInfo(this.product)
            .pipe(map(data => {
              console.log('*** EditProductComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
              this._prodId = data['PRODID'];

              /*
               * TODO: route to link with newly obtianed PRODID to reload all staff.
               */
              this.loadProductContent();
            }))
              .subscribe(() => {

                console.log('*** EditProductComponent onConfirm: *** POST *** ');
              });
        }
        break;

      case 2:
        console.log('*** EditProductComponent onConfirmCurr: (Parts) ');
        break;

      case 3:
        break;

      default:
        break;
    }
  }

  /** Shows a toast with the selected chart data value. */
  selectProductAnalyticsData(event) {
    this.messageService.add(
      {
        severity: 'info',
        summary: 'Data Selected',
        'detail': this.dataProductAnalytic.datasets[event.element._datasetIndex].data[event.element._index].toString()
      });
  }

  /** PrimeNG table state-save callback (currently a no-op). */
  onStateSave(state) {
    //    console.log('*** EditProductComponent onStateSave: ' + JSON.stringify(state));
  }

  /** Collapses all accordion tabs by resetting `tabActiveState`. */
  closeAllTabs() {
    this.editProdAcc.tabs.forEach(e => {
      console.log(e.header);

      this.tabActiveState[e.findTabIndex()] = false;
    });

  }

  /** Collapses all tabs and opens the bill-of-materials tab. */
  openBillOfMaterials(event) {
    console.log('*** EditProductComponent openBillOfMaterials: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[1] = true;
  }

  /** Collapses all tabs and opens the booking/reservation tab. */
  openBooking(event) {
    console.log('*** EditProductComponent openBooking: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[2] = true;
  }

  /** Collapses all tabs and opens the analytics chart tab. */
  openAnalytics(event) {
    console.log('*** EditProductComponent openAnalytics: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[3] = true;
  }

  /** Collapses all tabs and opens the price-calculation tab. */
  openProductPrice(event) {
    console.log('*** EditProductComponent openProductPrice: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[4] = true;
  }

  /** Collapses all tabs and opens the turnover sheet tab. */
  openTurnoverSheet(event) {
    console.log('*** EditProductComponent openTurnoverSheet: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[6] = true;
  }

  /** Collapses all tabs and opens the "used-in products" (parent products) tab. */
  openHistoryOfUse(event) {
    console.log('*** EditProductComponent openHistoryOfUse: ' + JSON.stringify(event));
    this.closeAllTabs();

    this.tabActiveState[9] = true;
  }

  /** Updates `PRODPRICEVID` from the selected vendor row and triggers a combined save (case 4 → 1). */
  onSetMainPriceRow(event) {
    console.log('*** EditProductComponent onSetMainPriceRow: ' + JSON.stringify(event));
    this.product.PRODPRICEVID = event.VENDORSITEMID;
    this.onConfirm(4);
  }

  /** POSTs a server-side copy of this product and routes to the new product's editor. */
  doCopyProduct(event: Product) {
    this._prodPutQuery =
      this.prodApi.postCopyProductInfo(this.product)
        .pipe()
          .subscribe((data) => {
            console.log('*** EditProductComponent doCopyProduct: *** on Post data map *** ' + JSON.stringify(data));
            let newProdId = data[''];  // Unnamed column
            /*
             * TODO: route to link with newly obtianed PRODID to reload all staff.
             */
            this.router.navigate(['/edit-product', newProdId ]);
          });
  }

  /**
   * Exports every field of the currently loaded (saved) product to a one-sheet .xlsx
   * (columns Field / Value, one row per Product property). Uses `lastConfirmedProduct`, the
   * last state loaded from or successfully saved to the server, so neither unsaved edits nor a
   * failed save are exported (B-40).
   */
  exportProduct() {
    const source = this.lastConfirmedProduct;
    if (!source) {
      return;
    }

    const toCell = (value: unknown): string | number | boolean => {
      if (value === null || value === undefined) {
        return '';
      }
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? '' : value.toISOString();
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return value as string | number | boolean;
    };

    const rows = Object.keys(source).map(key => ({ 'Field': key, 'Value': toCell(source[key]) }));

    // Keep letters/digits/underscore/dash/dot only; anything else would break the file name.
    const safeCode = String(source.PRODCODE || source.PRODID || 'product').trim().replace(/[^\w.-]+/g, '_');

    // xlsx is loaded lazily like every other export in the app, but file-saver is a CommonJS module: a
    // dynamic import('file-saver') only exposes `default`, so `.saveAs` was undefined and the click silently
    // did nothing. It is imported statically (as the other export components do) instead.
    import('xlsx').then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(rows, { header: ['Field', 'Value'] });
      const workbook = { Sheets: { product: worksheet }, SheetNames: ['product'] };
      const excelBuffer: ArrayBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(data, 'product_' + safeCode + '.xlsx');
    }).catch(err => {
      console.error('*** EditProductComponent exportProduct failed', err);
      this.messageService.add({ severity: 'error', summary: 'Export failed', detail: String(err && err.message ? err.message : err) });
    });
  }

  /** Pushes the current product code to `prodCodeCheck$` for debounced uniqueness validation. */
  checkProdCode(event) {
    if (this.product) {
      this.prodCodeCheck$.next(this.product.PRODCODE.trim());
    }
  }

  /** Re-spreads `product` into `kickChanges` to notify child components that parts have changed. */
  onProdPartsUpdated(event) {
    console.log('*** EditProductComponent onProdPartsUpdated: ' + JSON.stringify(event));
    this.kickChanges = { ... this.product };
  }

  /** Re-spreads `product` into `kickChanges` to notify child components that pricing has changed. */
  onProductPriceUpdate(event) {
    console.log('*** EditProductComponent onProductPriceUpdate: ' + JSON.stringify(event));
    this.kickChanges = { ... this.product };
  }

  /** Applies the selected product category ID to `product.PRODCATEGORY`. */
  onSelectedProductCategory(event) {
    console.log('*** EditProductComponent onSelectedProductCategory: ' + JSON.stringify(event));
    this.product.PRODCATEGORY = event;
  }

  /** Triggers a reload of parent products that use this product as a component. */
  onLoadParentProducts(event) {
    console.log('*** EditProductComponent onLoadParentProducts: ' + JSON.stringify(event));

    // getProductByParentProduct

    this.parentProductCheck$.next(this._prodId);
  }

  /** Logs changes to the approved customs code list (diagnostic). */
  onActcChange(event) {
    console.log('*** EditProductComponent onActcChange: ' + JSON.stringify(event));
  }

  /** Logs clicks on the approved customs code table (diagnostic). */
  onActcClick(event) {
    console.log('*** EditProductComponent onActcClick: ' + JSON.stringify(event));
  }

  /** Prepends the current `PRODCUSTCODE` to `ACTC_LIST` and POSTs the approval to the server. */
  approveCustomsCode(event) {
    console.log('*** EditProductComponent approveCustomsCode: ' + JSON.stringify(event));

    if (this._prodId) {

      this.product.ACTC_LIST = [
        {
          ACTC_DATE: new Date(),
          ACTC_FLAG: 0,
          ACTC_SOURCE: "Manual",
          ACTC_STRING: this.product.PRODCUSTCODE,
          PRODID: this.product.PRODID,
          PERSID: this.globalSettinsData.mainPersonId
        },
        ...this.product.ACTC_LIST];

      this._prodPutQuery =
        this.prodApi.postActcListItem(this.product).pipe(
          tap(data => console.log('*** EditProductComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data))),
          finalize(() => {
            this.loadProductContent();
          }))
          .subscribe();
    }
  }
  /** Copies the given text to the clipboard using the Clipboard API. */
  copyClipBoard(event) {
    console.log('*** EditProductComponent copyClipBoard: ' + JSON.stringify(event));
    if (navigator.clipboard) {
      navigator.clipboard.writeText(event)
        .then(() => console.log('*** EditProductComponent copyClipBoard: ' + 'Text copied!'))
        .catch(err => console.error('Failed to copy text:', err));
    } else {
      // Fallback for older browsers (e.g., using document.execCommand('copy'))
      // This method is generally discouraged due to deprecation and limitations.
    }
  }

  /**
   * Checks whether the product code may be edited. Queries for stock-movement documents
   * (DOCTYPE 2–5) first; if only invoices (DOCTYPE 1) exist, checks further for internal
   * transfer documents whose `DOCNOTE2` contains the product code. Sets `editCodeAllowed`
   * and shows an error toast if editing must be forbidden.
   */
  onAskEditing() {
    console.log('*** EditProductComponent onAskEditing: ');

    this.editCodeAllowed = false;
    let doNextCheck: boolean = true;
    let localAllowEditing: boolean = false;

    this._prodPutQuery =
      this.docApi.findDocumentsByParameters([{ mode: 'PRODID', value: this.product.PRODID }, { mode: 'DOCTYPE', value: [1, 2, 3, 4, 5] }]).pipe(

        tap(docs => environment.production || (console.table(docs))),

        map(docs => {
          if (docs.length == 0) {

            // There is no documents linked to product at all
            // Lets do anything we want

            // Honestly - could be exist internal movement of parts with this product as component
            // But without parent documenst it is very less probable

            localAllowEditing = true;
            doNextCheck = false;
          } else {
            // There are some documents linked to product
            // Need to check them

            let thereIsStockMovement: boolean = false;
            docs.forEach(doc => {
              if ([2, 3, 4, 5].includes(doc.DOCTYPE)) {
                thereIsStockMovement = true;
              }
            })

            if (thereIsStockMovement) {
              // There is stock movement - definitely forbid editing
              localAllowEditing = false;
              doNextCheck = false;
              return;
            }
            // No stock movements - could be sales/purchase invoices only

            let onlyInvoices: boolean = true;
            docs.forEach(doc => {
              if (doc.DOCTYPE != 1) {
                onlyInvoices = false;
              }
            });

            if (onlyInvoices) {
              // There are only invoices - allow editing with warning
              localAllowEditing = true;
              doNextCheck = true;
              return;
            }
          }
        }),

        filter(() => doNextCheck),

        // Here we have to check also internal movements of components of this product
        // Internal movements have NOTE2 = contatins PRODCODE of this product

        concatMap(() => this.docApi.findDocumentsByParameters([
          { mode: 'DOCTYPE', value: [5] },
          { mode: 'DOCFIELD', value: { fieldName: 'DOCNOTE2', operMode: 'contain', fieldValue: this.product.PRODCODE } }
        ]).pipe(

          tap(docs => environment.production || (console.table(docs))),

          map(docs => {
            if (docs.length > 0) {
              // There are some internal movements of components - forbid editing
              localAllowEditing = false;
              doNextCheck = false;
            } else {
              // No internal movements of components - allow editing
              localAllowEditing = true;
              doNextCheck = true;
            }
          })
        )),

        finalize(() => {
          // Checking finished - emit event
          this.editCodeAllowed = localAllowEditing;

          console.log('*** EditProductComponent onAskEditing: ===> emit allowEditing=' + this.editCodeAllowed);
          if (!this.editCodeAllowed) {
            this.messageService.add({ severity: 'error', summary: 'Редактирование запрещено', detail: 'Товар связан с движением на складе' });  
          }
        })

      ).subscribe();
  }

  /** Clears or resets the given table's filters — workaround to force reload when switching tabs. */
  clearOrResetTableFilter(event, dt: Table) {
    console.log('*** EditProductComponent clearOrResetTableFilter: ' + JSON.stringify(event));
    if (dt) {
      if (dt.filters && Object.keys(dt.filters).length > 0) {
        dt.clear();
      } else {
        dt.reset();
      }
    }
  }
 
  private handleError(operation: string) {
    return (error: any) => {
      this.messageService.add({
        severity: 'error',
        summary: `${operation} Failed`,
        detail: error.message || 'Unknown error'
      });
      return throwError(() => error);
    };
  }
}

